/**
 * Review History Panel
 * 
 * Displays timeline of review events, revision cycles,
 * and allows exporting review history.
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import { LoadingSpinner } from '../UI/LoadingSpinner';
import { Select } from '../UI/Select';
import { 
  History, 
  Filter,
  Clock,
  User,
  FileText,
  CheckCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import { 
  getReviewTimeline,
  type ReviewTimeline
} from '../../api/reviewApi';

export interface ReviewHistoryPanelProps {
  reportId: string;
}

export function ReviewHistoryPanel({ reportId }: ReviewHistoryPanelProps) {
  // Always use timeline view (removed view mode toggle)
  const [filterAction, setFilterAction] = useState<string>('all');

  // Get review timeline (always enabled)
  const { data: timeline, isLoading: timelineLoading } = useQuery({
    queryKey: ['review-timeline', reportId],
    queryFn: () => getReviewTimeline(reportId),
    enabled: !!reportId,
  });

  const isLoading = timelineLoading;

  const actionColors: Record<string, string> = {
    submitted: 'info',
    assigned: 'default',
    started: 'info',
    completed: 'success',
    approved: 'success',
    rejected: 'error',
    revision_requested: 'warning',
    re_submitted: 'info',
  };

  const actionIcons: Record<string, any> = {
    submitted: FileText,
    assigned: User,
    started: Clock,
    completed: CheckCircle,
    approved: CheckCircle,
    rejected: XCircle,
    revision_requested: RefreshCw,
    re_submitted: RefreshCw,
  };

  // Always show timeline view (removed export and other view modes)
  const filteredTimeline = timeline?.filter((event: ReviewTimeline) => 
    filterAction === 'all' || event.action === filterAction
  ) || [];

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading review history...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="mb-6">
        <h3 className="text-lg font-semibold">Review History</h3>
      </div>

      {/* Filter */}
      <div className="mb-4 flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <Select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          options={[
            { value: 'all', label: 'All Actions' },
            { value: 'submitted', label: 'Submitted' },
            { value: 'assigned', label: 'Assigned' },
            { value: 'started', label: 'Started' },
            { value: 'completed', label: 'Completed' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'revision_requested', label: 'Revision Requested' },
            { value: 're_submitted', label: 'Re-submitted' },
          ]}
        />
      </div>

      {/* Timeline View (always shown) */}
      {(
        <div className="space-y-3">
          {filteredTimeline.length > 0 ? (
            filteredTimeline.map((event: ReviewTimeline) => {
              const ActionIcon = actionIcons[event.action] || History;
              return (
                <div
                  key={event.id}
                  className="flex items-start gap-4 p-4 border border-theme-border rounded-lg bg-theme-surface"
                >
                  <div className="flex-shrink-0">
                    <div className={`p-2 rounded-lg bg-${actionColors[event.action] || 'default'}-100 dark:bg-${actionColors[event.action] || 'default'}-900/20`}>
                      <ActionIcon className={`h-5 w-5 text-${actionColors[event.action] || 'default'}-600 dark:text-${actionColors[event.action] || 'default'}-400`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={actionColors[event.action] || 'default'} size="sm">
                        {event.action}
                      </Badge>
                      <span className="text-sm font-medium">{event.performed_by_name}</span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {new Date(event.performed_at).toLocaleString()}
                    </div>
                    {event.notes && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{event.notes}</p>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No timeline events found</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
